import { create } from 'zustand'
import type { Poll, PollVote, PollType } from '@vc/models/poll'
import { rememberPrompted } from '../services/poll-prompt-memory'
import { pollsApi } from '@/features/meetings/services/meetings.api'
import { useMeetingSessionStore } from './meeting-session.store'

/** The room this session is in and its room token, when it holds credentials. */
function roomAccess(): { roomId?: string; roomToken?: string } {
  const credentials = useMeetingSessionStore.getState().roomCredentials
  return {
    roomId: credentials?.channelTopic?.replace(/^room:/, ''),
    roomToken: credentials?.roomToken,
  }
}

/**
 * Polls in the current meeting.
 *
 * This store is a **mirror**, not a source of truth. The server owns polls and
 * their tallies; every action here writes through the API and the room channel
 * pushes the authoritative poll back to every participant, this one included.
 * It used to mutate locally, which meant a poll the host raised existed only in
 * the host's own browser — nobody else ever saw it.
 *
 * Counts are never computed here. `poll.totalVotes` and each option's `votes`
 * arrive already tallied, so a re-vote or a missed broadcast cannot leave this
 * client showing a total nobody else has.
 */

type CreatePollInput = {
  question: string
  type: PollType
  options: string[]
  anonymous: boolean
  maxSelections?: number
}

type MeetingPollsState = {
  polls: Poll[]
  activePollId: string | null
  /** Local record of what *this* participant voted, for the "you voted" UI. */
  pollVotes: PollVote[]
  /** Set once the meeting mounts; every write needs it. */
  meetingId: string | null

  setMeetingId: (id: string | null) => void
  setActivePollId: (id: string | null) => void
  /** Replace everything — used by the catch-up fetch on join. */
  setPolls: (polls: Poll[]) => void
  /** Insert or replace one poll — used by every `poll:*` broadcast. */
  upsertPoll: (poll: Poll) => void
  /**
   * Add the votes the catch-up fetch says this participant already cast, so a
   * reload does not make an answered poll look unanswered. A vote cast here
   * since then is newer and wins.
   */
  addOwnVotes: (votes: PollVote[]) => void

  /**
   * Polls raised from *this* client. The author is not prompted to answer their
   * own question — they vote from the Polls panel, where the option already is.
   *
   * Recorded here rather than compared against `poll.createdBy`, which is a
   * display name, while the local participant is keyed by SFU peer id. Those
   * two never reliably match, and a name comparison would silence the prompt
   * for anyone who happens to share the author's name.
   */
  authoredPollIds: string[]

  /**
   * A poll that arrived *live*, on the room channel, while we were sitting in
   * the meeting — the only thing that should interrupt anyone.
   *
   * Set by the `poll:created` broadcast and by nothing else. The catch-up fetch
   * deliberately does not touch it: a participant joining late, or reloading
   * mid-poll, receives the same polls through `setPolls`, and being ambushed by
   * a dialog for a question asked before you arrived is not a prompt, it is an
   * interruption. They vote from the Polls panel instead.
   *
   * Cleared once consumed, so it fires once and does not survive a re-render.
   */
  promptPollId: string | null
  setPromptPollId: (id: string | null) => void

  handleCreatePoll: (data: CreatePollInput) => Promise<void>
  handleVotePoll: (
    pollId: string,
    optionIds: string[],
    participantId: string,
    participantName: string,
  ) => Promise<void>
  handleEndPoll: (pollId: string) => Promise<void>
}

export const useMeetingPollsStore = create<MeetingPollsState>((set, get) => ({
  polls: [],
  activePollId: null,
  pollVotes: [],
  meetingId: null,
  authoredPollIds: [],
  promptPollId: null,

  setPromptPollId: (promptPollId) => set({ promptPollId }),

  setMeetingId: (meetingId) => set({ meetingId }),
  setActivePollId: (activePollId) => set({ activePollId }),

  setPolls: (polls) =>
    set((s) => ({
      polls,
      // Surface the newest still-open poll, so someone arriving mid-poll is
      // shown it rather than having to go looking in the panel.
      activePollId:
        s.activePollId && polls.some((p) => p.id === s.activePollId)
          ? s.activePollId
          : ([...polls].reverse().find((p) => p.isActive)?.id ?? null),
    })),

  upsertPoll: (poll) =>
    set((s) => {
      const known = s.polls.some((p) => p.id === poll.id)
      const polls = known
        ? s.polls.map((p) => (p.id === poll.id ? poll : p))
        : [...s.polls, poll]
      // A new open poll takes focus; the one being closed gives it up.
      const activePollId = poll.isActive
        ? !known
          ? poll.id
          : s.activePollId
        : s.activePollId === poll.id
          ? null
          : s.activePollId
      return { polls, activePollId }
    }),

  addOwnVotes: (votes) =>
    set((s) => ({
      pollVotes: [
        ...s.pollVotes,
        ...votes.filter(
          (v) => !s.pollVotes.some((p) => p.pollId === v.pollId && p.participantId === v.participantId),
        ),
      ],
    })),

  handleCreatePoll: async (data) => {
    const meetingId = get().meetingId
    if (!meetingId) return
    const body = {
      question: data.question,
      type: data.type,
      options: data.options,
      anonymous: data.anonymous,
      maxSelections: data.maxSelections,
    }
    // Same reasoning as voting below: a co-host promoted from a guest has room
    // credentials but no app session, so the authed route 401'd them. Using the
    // room path whenever we can also keeps `createdById` the same id we vote
    // with, which is what refuses the author a vote on their own poll.
    const { roomId, roomToken } = roomAccess()
    const { poll } =
      roomToken && roomId
        ? await pollsApi.createByRoom(roomId, roomToken, body)
        : await pollsApi.create(meetingId, body)
    // Mark it ours BEFORE it lands in state: upsertPoll makes it the active
    // poll, which is what the prompt watches. Recording afterwards would leave
    // a frame in which the author's own question is up for prompting.
    set((s) => ({ authoredPollIds: [...s.authoredPollIds, (poll as Poll).id] }))
    // Remembered on disk as well, because `authoredPollIds` lives in memory and
    // a refresh empties it. The host who wrote the question was then prompted
    // to answer it the moment they came back — `poll.createdBy` cannot rescue
    // that, being a display name against a peer id (see the note on
    // `authoredPollIds`). Filed under the same memory as a shown prompt: from
    // the prompt's point of view, writing a question and being asked one both
    // mean you already know about it.
    rememberPrompted(get().meetingId, (poll as Poll).id)
    // Apply our own copy immediately rather than waiting for the broadcast to
    // come back round — the creator should not watch their own poll lag.
    get().upsertPoll(poll as Poll)
  },

  handleVotePoll: async (pollId, optionIds, participantId, participantName) => {
    const meetingId = get().meetingId
    if (!meetingId) return

    // Vote as a room participant whenever we hold room credentials, which is
    // everyone actually in the meeting. The authed route needs an app session
    // that a guest joining by link does not have, so their vote used to 401 in
    // silence: the poll opened, they answered, and nothing was recorded.
    //
    // One path for everyone also means one identity per voter. The two routes
    // key votes differently — app user id vs room-token sub — so choosing
    // between them per call would let the same person vote twice.
    const { roomId, roomToken } = roomAccess()
    const { poll } =
      roomToken && roomId
        ? await pollsApi.voteByRoom(roomId, roomToken, pollId, optionIds)
        : await pollsApi.vote(meetingId, pollId, optionIds)
    set((s) => ({
      pollVotes: [
        ...s.pollVotes.filter((v) => v.pollId !== pollId || v.participantId !== participantId),
        { pollId, participantId, participantName, optionIds, timestamp: Date.now() },
      ],
    }))
    get().upsertPoll(poll as Poll)
  },

  handleEndPoll: async (pollId) => {
    const meetingId = get().meetingId
    if (!meetingId) return
    const { roomId, roomToken } = roomAccess()
    const { poll } =
      roomToken && roomId
        ? await pollsApi.endByRoom(roomId, roomToken, pollId)
        : await pollsApi.end(meetingId, pollId)
    get().upsertPoll(poll as Poll)
  },
}))

export const selectActivePoll = (s: MeetingPollsState): Poll | null => {
  if (!s.activePollId) return null
  return s.polls.find((p) => p.id === s.activePollId) ?? null
}
